import { CSSProperties } from "react";

import { getAssetFullUrl } from "../../../helpers/utils";

/**
 * Ảnh đại diện của cơ thủ, dùng chung cho trang giải và màn quét QR.
 *
 * Tách khỏi live.tsx vì màn "thông tin trận đấu" sau khi quét QR nằm ở file khác
 * nhưng phải hiện đúng một kiểu ảnh — chép sang là hai nơi rơi về chữ cái đầu
 * theo hai cách khác nhau, rồi lệch dần.
 */

/**
 * Cơ thủ đang xem ở chế độ phóng lớn.
 *
 * `url` CÓ THỂ null: chưa có ảnh thì lớp phủ hiện chữ cái đầu cỡ lớn. Trước đây
 * không có ảnh là không bấm được, mà thứ người ta đi tìm khi chạm vào một cơ thủ
 * lạ là HẠNG và ĐIỂM của người đó — hai thứ luôn có, không phụ thuộc vào việc họ
 * đã tải ảnh lên hay chưa.
 */
export type AnhPhongTo = {
  url: string | null;
  name: string;
  /** lớp hạng đang đeo: "G1", "E3"… (ranks.class, không phải ranks.code) */
  rankClass?: string | null;
  /** điểm xếp hạng Biadi. */
  score?: number | null;
};

/** Chữ cái đầu của TÊN (phần cuối), dùng khi cơ thủ chưa có ảnh. */
export const initials = (name: string) => {
  const parts = (name || "").trim().split(/\s+/);

  return parts.length ? parts[parts.length - 1][0] : "?";
};

/**
 * Chưa có ảnh thì rơi về chữ cái đầu tên, nên màn hình không bao giờ trống một ô
 * tròn. Có chỗ nhận sự kiện thì bấm vào xem thẻ cơ thủ — ảnh ở đây chỉ 26–56px,
 * không đủ để nhận ra mặt người, và hạng với điểm thì không hiện ở đây chút nào.
 *
 * Bấm được KỂ CẢ KHI CHƯA CÓ ẢNH: thẻ mở ra còn mang hạng và điểm, mà hai thứ đó
 * luôn có. Buộc phải có ảnh mới bấm được nghĩa là cơ thủ nào chưa tải ảnh lên thì
 * không ai tra được trình của họ — trong khi họ vẫn đứng đó trên bảng xếp hạng.
 *
 * Dùng div chứ không dùng button: preflight của Tailwind nạp SAU shared/league.css và đặt
 * lại background của thẻ button, nên một nút ở đây sẽ mất nền gradient.
 */
export const Avatar = ({
  src,
  name,
  size = "ava",
  me = false,
  style,
  rankClass,
  score,
  onZoom,
}: {
  src?: string | null;
  name: string;
  /** avah: cỡ nhân vật chính, chỉ dùng ở màn thông tin trận đấu sau khi quét QR. */
  size?: "ava" | "avas" | "avah";
  me?: boolean;
  style?: CSSProperties;
  rankClass?: string | null;
  score?: number | null;
  onZoom?: (anh: AnhPhongTo) => void;
}) => {
  const url = getAssetFullUrl(src ?? null);
  const phongToDuoc = !!onZoom;

  return (
    <div
      className={`${size}${me ? " me" : ""}${phongToDuoc ? " zoomable" : ""}`}
      style={style}
      role={phongToDuoc ? "button" : undefined}
      aria-label={phongToDuoc ? `Xem thông tin của ${name}` : undefined}
      onClick={phongToDuoc ? () => onZoom!({ url: url ?? null, name, rankClass, score }) : undefined}
    >
      {url ? <img alt={name} src={url} /> : initials(name)}
    </div>
  );
};

/** Một ô số liệu trong thẻ phóng lớn; thiếu dữ liệu thì in gạch chứ không bỏ ô. */
const OSoLieu = ({ nhan, gia_tri }: { nhan: string; gia_tri: string }) => (
  <div className="avazoom-o">
    <span className="k">{nhan}</span>
    <b>{gia_tri}</b>
  </div>
);

/**
 * Lớp phủ xem cơ thủ ở cỡ lớn; chạm chỗ nào cũng đóng.
 *
 * Hai ô số liệu LUÔN hiện, kể cả khi thiếu dữ liệu thì in dấu gạch. Ẩn ô đi khi
 * thiếu thì thẻ đổi bố cục theo từng người, và người xem không biết là cơ thủ này
 * chưa có hạng hay là ứng dụng quên hiện.
 */
export const AvatarZoom = ({ anh, onClose }: { anh: AnhPhongTo | null; onClose: () => void }) => {
  if (!anh) {
    return null;
  }

  return (
    <div className="avazoom" onClick={onClose}>
      {anh.url ? (
        <img alt={anh.name} src={anh.url} />
      ) : (
        /* Chưa có ảnh: vẫn là một ô tròn đúng cỡ ảnh, không phải một khoảng trống.
           Thẻ giữ nguyên bố cục dù cơ thủ đã tải ảnh lên hay chưa. */
        <div className="avazoom-chu" aria-label={anh.name}>
          {initials(anh.name)}
        </div>
      )}
      <div className="avazoom-nm">{anh.name}</div>
      <div className="avazoom-meta">
        <OSoLieu nhan="Xếp hạng" gia_tri={anh.rankClass || "—"} />
        <OSoLieu
          nhan="Điểm"
          gia_tri={
            anh.score === null || anh.score === undefined ? "—" : anh.score.toLocaleString("vi-VN")
          }
        />
      </div>
      <div className="tiny" style={{ marginTop: 6 }}>
        Chạm để đóng
      </div>
    </div>
  );
};
